import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Button, Typography, Checkbox, message, Grid, Select } from 'antd';
import { THEME_COLORS, COLORS, GRADIENTS } from '../../../utils/colors';
import PrivacyPolicyModal from '../../../components/common/PrivacyPolicyModal';
import TermsOfServiceModal from '../../../components/common/TermsOfServiceModal';
import styles from './DealershipDetailsStep.module.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const { Text } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const DEALER_TYPES = [
  { value: 'independent', label: 'Independent' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'wholesale', label: 'Wholesale' }
];

function MagicWandIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 4V2" /><path d="M15 16v-2" /><path d="M8 9h2" /><path d="M20 9h2" /><path d="M17.8 11.8L19 13" /><path d="M15 9h0" /><path d="M17.8 6.2L19 5" /><path d="M3 21l9-9" /><path d="M12.2 6.2L13 5" /><path d="m3 21 3-3" /><path d="m3 21 2-2" /><path d="m3 21 4-4" />
    </svg>
  );
}

function CheckIconSvg() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

const DealershipDetailsStep = ({ onNext, formData = {} }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();

  const [setupMode, setSetupMode] = useState('website');
  const [skipAutofillClicked, setSkipAutofillClicked] = useState(false);
  const [autofillFailed, setAutofillFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [agreements, setAgreements] = useState(formData.agreements || {
    dataUsage: false,
    privacyTerms: false
  });
  const [disabled, setDisabled] = useState(true);
  const [autoFilledData, setAutoFilledData] = useState(null);
  const [privacyPolicyVisible, setPrivacyPolicyVisible] = useState(false);
  const [termsOfServiceVisible, setTermsOfServiceVisible] = useState(false);
  const hasSyncedFormData = useRef(false);

  useEffect(() => {
    if (!autoFilledData) return;
    form.setFieldsValue({
      name: autoFilledData.name || '',
      zipcode: autoFilledData.zip || autoFilledData.zipcode || '',
      phone: autoFilledData.phone || '',
      address_line1: autoFilledData.street || autoFilledData.address_line1 || '',
      city: autoFilledData.city || '',
      state: autoFilledData.state || '',
      dealer_type: autoFilledData.dealer_type || undefined
    });
  }, [autoFilledData, form]);

  const website = Form.useWatch('website', form);
  const name = Form.useWatch('name', form);
  const phone = Form.useWatch('phone', form);
  const address_line1 = Form.useWatch('address_line1', form);
  const city = Form.useWatch('city', form);
  const state = Form.useWatch('state', form);
  const zipcode = Form.useWatch('zipcode', form);
  const dealer_type = Form.useWatch('dealer_type', form);

  useEffect(() => {
    if (hasSyncedFormData.current) return;
    hasSyncedFormData.current = true;
    if (formData && Object.keys(formData).length > 0) {
      form.setFieldsValue({
        name: formData.name,
        website: formData.website,
        zipcode: formData.zipcode || formData.zip,
        phone: formData.phone || formData.phone_number,
        address_line1: formData.address_line1,
        city: formData.city,
        state: formData.state,
        dealer_type: formData.dealer_type
      });
    }
  }, [form, formData]);

  const websiteFilled = website && String(website).trim() !== '' && website !== 'https://';

  // Function to check if form is valid (for website mode Continue button)
  const checkFormValidity = useCallback(() => {
    const websiteFilledLocal = website && String(website).trim() !== '' && website !== 'https://';
    if (!websiteFilledLocal) {
      setDisabled(true);
      return;
    }
    if (!autoFilledData && !skipAutofillClicked) {
      setDisabled(true);
      return;
    }
    const nameVal = (name ?? autoFilledData?.name ?? '').toString().trim();
    const phoneVal = (phone ?? autoFilledData?.phone ?? '').toString().trim();
    const addressVal = (address_line1 ?? autoFilledData?.street ?? autoFilledData?.address_line1 ?? autoFilledData?.address ?? '').toString().trim();
    const cityVal = (city ?? autoFilledData?.city ?? '').toString().trim();
    const stateVal = (state ?? autoFilledData?.state ?? '').toString().trim();
    const zipVal = (zipcode ?? autoFilledData?.zip ?? autoFilledData?.zipcode ?? '').toString().trim();
    const hasAllRequiredFields = Boolean(nameVal !== '' && phoneVal !== '' && addressVal !== '' && cityVal !== '' && stateVal !== '' && zipVal !== '');
    setDisabled(!hasAllRequiredFields);
  }, [website, autoFilledData, skipAutofillClicked, name, phone, address_line1, city, state, zipcode]);

  useEffect(() => {
    checkFormValidity();
  }, [checkFormValidity]);

  const canContinueWebsite = websiteFilled && (autoFilledData != null || skipAutofillClicked);
  const manualRequiredFilled = Boolean(
    name && String(name).trim() !== '' &&
    address_line1 && String(address_line1).trim() !== '' &&
    city && String(city).trim() !== '' &&
    state && String(state).trim() !== '' &&
    zipcode && String(zipcode).trim() !== ''
  );
  const agreementsFilled = agreements.dataUsage && agreements.privacyTerms;
  const canContinueManual = manualRequiredFilled && agreementsFilled;
  const canContinue = setupMode === 'website' ? (canContinueWebsite && agreementsFilled) : canContinueManual;

  const handleAutoPopulate = useCallback(async () => {
    const websiteValue = website || form.getFieldValue('website');
    if (!websiteValue || websiteValue.trim() === '' || websiteValue === 'https://') {
      message.error('Please enter a website first');
      return;
    }
    const cleanWebsite = websiteValue.trim();
    setLoading(true);
    setAutofillFailed(false);
    try {
      const response = await fetch(
        `${BASE_URL}/dealership/details?inventory_url=${encodeURIComponent(cleanWebsite)}`,
        { method: 'GET', headers: { accept: 'application/json' } }
      );
      const raw = await response.json();
      if (!response.ok) {
        setAutofillFailed(true);
        message.error(raw?.detail || raw?.message || 'Unable to fetch dealership details.');
        return;
      }
      const notFound = raw.detail && String(raw.detail).includes('No dealership found');
      if (notFound) {
        setAutofillFailed(true);
        message.warning('We couldn\'t find that dealership.');
        return;
      }
      const data = raw.data ?? raw.result ?? raw.dealership ?? raw;
      const hasData = data && typeof data === 'object' && (data.name || data.marketcheck_id || data.street || data.city);
      if (hasData) {
        const values = {
          website: data.inventory_url || cleanWebsite,
          name: data.name ?? '',
          zipcode: data.zip ?? data.zipcode ?? '',
          phone: data.phone ?? '',
          address_line1: data.street ?? data.address_line1 ?? '',
          city: data.city ?? '',
          state: data.state ?? '',
          dealer_type: data.dealer_type ?? undefined
        };
        form.setFieldsValue(values);
        setAutoFilledData(data);
        message.success('Dealership information auto-populated.');
      } else {
        setAutofillFailed(true);
      }
    } catch (err) {
      console.error('Auto-fill error:', err);
      setAutofillFailed(true);
      message.error('Failed to auto-populate.');
    } finally {
      setLoading(false);
    }
  }, [website, form]);

  const handleSubmit = useCallback(async () => {
    if (!canContinue) return;
    try {
      setValidating(true);
      const formValues = form.getFieldsValue();

      if (setupMode === 'website') {
        const websiteValue = (website || formValues.website || '').trim();
        if (!websiteValue || websiteValue === 'https://') {
          message.error('Please enter a valid website');
          setValidating(false);
          return;
        }
        const response = await fetch(
          `${BASE_URL}/dealership/details?inventory_url=${encodeURIComponent(websiteValue)}`,
          { method: 'GET', headers: { accept: 'application/json' } }
        );
        if (!response.ok) {
          message.error('Unable to validate dealership.');
          setValidating(false);
          return;
        }
        const apiData = await response.json();
        if (apiData.detail && apiData.detail.includes('No dealership found')) {
          message.error('Unable to validate dealership.');
          setValidating(false);
          return;
        }
        const dealershipData = apiData.data || {};
        onNext({
          agreements,
          name: formValues.name || dealershipData.name || '',
          marketcheck_id: dealershipData.marketcheck_id,
          phone_number: formValues.phone || dealershipData.phone || '',
          website: dealershipData.inventory_url || websiteValue,
          address_line1: formValues.address_line1 || dealershipData.street || '',
          city: formValues.city || dealershipData.city || '',
          state: formValues.state || dealershipData.state || '',
          zipcode: formValues.zipcode || dealershipData.zip || '',
          latitude: dealershipData.latitude,
          longitude: dealershipData.longitude,
          dealer_type: formValues.dealer_type
        });
      } else {
        onNext({
          agreements,
          name: formValues.name || '',
          marketcheck_id: undefined,
          phone_number: formValues.phone || '',
          website: '',
          address_line1: formValues.address_line1 || '',
          city: formValues.city || '',
          state: formValues.state || '',
          zipcode: formValues.zipcode || '',
          latitude: undefined,
          longitude: undefined,
          dealer_type: formValues.dealer_type
        });
      }
    } catch (err) {
      console.error('Submit error:', err);
      message.error('Something went wrong.');
    } finally {
      setValidating(false);
    }
  }, [canContinue, setupMode, website, form, agreements, onNext]);

  const handleAgreementChange = (key, checked) => {
    setAgreements(prev => ({ ...prev, [key]: checked }));
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    color: '#fff',
    height: '44px',
    padding: '0 14px',
    fontSize: '14px'
  };

  const labelStyle = { color: 'rgba(255,255,255,0.7)', fontWeight: 500, fontSize: '12px', marginBottom: 6 };

  const rightSideCards = (
    <div className={styles.rightCol}>
      <div className={styles.sideCard}>
        <div className={styles.sideCardTitle}>
          <ShieldIcon />
          What We'll Pull
        </div>
        <ul className={styles.sideCardList}>
          <li><CheckIconSvg className={styles.checkIcon} /> Dealer name, address, phone</li>
          <li><CheckIconSvg className={styles.checkIcon} /> Inventory categories (when available)</li>
        </ul>
        <div className={styles.dataSafeBlock}>
          <div className={styles.dataSafeTitle}>
            <ShieldIcon />
            Is my data safe?
          </div>
          <p className={styles.dataSafeText}>
            Yes, we only use it to pre-fill your profile— and we'll never share it.
          </p>
        </div>
      </div>
      <div className={styles.sideCard}>
        <div className={styles.sideCardTitle}>
          <ShieldIcon />
          Consent & Terms
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
            <Checkbox
              checked={agreements.dataUsage}
              onChange={e => handleAgreementChange('dataUsage', e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>I consent to anonymized data usage to improve Hooptie</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
            <Checkbox
              checked={agreements.privacyTerms}
              onChange={e => handleAgreementChange('privacyTerms', e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              I accept the{' '}
              <Text type="link" onClick={() => setPrivacyPolicyVisible(true)} style={{ color: COLORS.PRIMARY[400], padding: 0 }}>Privacy Policy</Text>
              {' '}and{' '}
              <Text type="link" onClick={() => setTermsOfServiceVisible(true)} style={{ color: COLORS.PRIMARY[400], padding: 0 }}>Terms of Service</Text>
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.wrap}>
      <style>
        {`
          .dealership-input::placeholder {
            color: rgba(255,255,255,0.45);
          }
          .dealership-input:focus, .dealership-input.ant-input-focused {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 2px rgba(59,130,246,0.2) !important;
          }
          .dealership-select .ant-select-selector {
            background: rgba(255,255,255,0.06) !important;
            border: 1px solid rgba(255,255,255,0.08) !important;
            border-radius: 10px !important;
            color: #fff !important;
            height: 44px !important;
          }
          .dealership-select.ant-select-focused .ant-select-selector {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 2px rgba(59,130,246,0.2) !important;
          }
        `}
      </style>

      <Form form={form} layout="vertical" onValuesChange={() => {}}>
        <div className={styles.grid}>
          <div className={styles.leftCol}>
            <div className={styles.formCard}>
              <h2 className={styles.cardTitle}>Dealership Profile</h2>
              <p className={styles.cardSubtitle}>Tell us about your dealership</p>

              {/* Setup mode toggle */}
              <div className={styles.toggleWrap}>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${setupMode === 'website' ? styles.toggleBtnActive : ''}`}
                  onClick={() => setSetupMode('website')}
                >
                  {setupMode === 'website' ? '● ' : ''}Use Website (Recommended)
                </button>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${setupMode === 'manual' ? styles.toggleBtnActive : ''}`}
                  onClick={() => setSetupMode('manual')}
                >
                  {setupMode === 'manual' ? '● ' : ''}Manually Enter
                </button>
              </div>

              {setupMode === 'website' && (
                <>
                  <h3 className={styles.setupHeading}>How should we set up your dealership?</h3>
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Website</label>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <Form.Item name="website" style={{ flex: '1 1 200px', marginBottom: 0, minWidth: 0 }}>
                        <Input
                          placeholder="Enter dealership website"
                          className="dealership-input"
                          style={inputStyle}
                        />
                      </Form.Item>
                      <Button
                        type="primary"
                        onClick={handleAutoPopulate}
                        loading={loading}
                        disabled={loading || !websiteFilled}
                        className={styles.autoPopulateBtn}
                        icon={<MagicWandIcon />}
                      >
                        Auto Populate
                      </Button>
                    </div>
                    <p className={styles.helperText}>
                      We'll pull dealer name, address, phone, and inventory categories (when available).
                    </p>
                  </div>

                  {autofillFailed && (
                    <div className={styles.failureAlert}>
                      <div className={styles.failureAlertTitle}>We couldn't pull everything from that site.</div>
                      <div className={styles.failureAlertSub}>No worries — finish the missing fields manually.</div>
                      <Button type="default" size="small" onClick={() => { setSetupMode('manual'); setAutofillFailed(false); }} style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff' }}>
                        Switch to Manual Entry
                      </Button>
                    </div>
                  )}

                </>
              )}

              {/* Manual fields: disabled/greyed only when website mode and not yet autofilled. Input/Select must be direct child of Form.Item for value to display. */}
              <div className={setupMode === 'website' && !autoFilledData ? styles.manualFieldsDisabled : undefined}>
                <Form.Item name="name" label={<span className={styles.inputLabel}>Dealership Name {setupMode === 'manual' && '*'}</span>} rules={[{ required: setupMode === 'manual', message: 'Required' }]} style={{ marginBottom: 16 }}>
                  <Input placeholder="e.g. Premier Auto Group" className="dealership-input" style={inputStyle} disabled={setupMode === 'website' && !autoFilledData} />
                </Form.Item>
                <Form.Item name="phone" label={<span className={styles.inputLabel}>Phone</span>} style={{ marginBottom: 16 }}>
                  <Input placeholder="(555) 123-4567" className="dealership-input" style={inputStyle} disabled={setupMode === 'website' && !autoFilledData} />
                </Form.Item>
                <Form.Item name="address_line1" label={<span className={styles.inputLabel}>Address line 1 {setupMode === 'manual' && '*'}</span>} rules={[{ required: setupMode === 'manual', message: 'Required' }]} style={{ marginBottom: 16 }}>
                  <Input placeholder="Street address" className="dealership-input" style={inputStyle} disabled={setupMode === 'website' && !autoFilledData} />
                </Form.Item>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                  <Form.Item name="city" label={<span className={styles.inputLabel}>City {setupMode === 'manual' && '*'}</span>} rules={[{ required: setupMode === 'manual', message: 'Required' }]} style={{ marginBottom: 16 }}>
                    <Input placeholder="City" className="dealership-input" style={inputStyle} disabled={setupMode === 'website' && !autoFilledData} />
                  </Form.Item>
                  <Form.Item name="state" label={<span className={styles.inputLabel}>State {setupMode === 'manual' && '*'}</span>} rules={[{ required: setupMode === 'manual', message: 'Required' }]} style={{ marginBottom: 16 }}>
                    <Select
                      placeholder="Select state"
                      className="dealership-select"
                      style={{ width: '100%' }}
                      optionFilterProp="children"
                      disabled={setupMode === 'website' && !autoFilledData}
                    >
                      {US_STATES.map(s => (
                        <Option key={s} value={s}>{s}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <Form.Item name="zipcode" label={<span className={styles.inputLabel}>Zip {setupMode === 'manual' && '*'}</span>} rules={[{ required: setupMode === 'manual', message: 'Required' }]} style={{ marginBottom: 16 }}>
                  <Input placeholder="ZIP code" className="dealership-input" style={inputStyle} disabled={setupMode === 'website' && !autoFilledData} />
                </Form.Item>
                <Form.Item name="dealer_type" label={<span className={styles.inputLabel}>Dealer type</span>} style={{ marginBottom: 16 }}>
                  <div className={styles.segmentedWrap}>
                      {DEALER_TYPES.map(d => (
                        <button
                          key={d.value}
                          type="button"
                          className={`${styles.segmentedBtn} ${dealer_type === d.value ? styles.segmentedBtnActive : ''}`}
                          onClick={() => form.setFieldValue('dealer_type', d.value)}
                          disabled={setupMode === 'website' && !autoFilledData}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                </Form.Item>
              </div>

            </div>
          </div>

          {!isMobile && rightSideCards}
        </div>

        {/* Mobile: consent cards below form */}
        {isMobile && (
          <div style={{ marginTop: 16 }}>
            {rightSideCards}
          </div>
        )}

        {/* CTA */}
        <div className={styles.ctaRow}>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={validating}
            disabled={!canContinue || validating || (setupMode === 'website' && disabled)}
            className={styles.primaryCta}
            style={{ background: GRADIENTS.PRIMARY, border: 'none' }}
          >
            Continue to Plan & Terms
          </Button>
        </div>
        <div className={styles.footerLine}>
          By continuing, you agree to Hooptie's <Link to="/terms-of-service" className={styles.footerLink}>Terms</Link> & <Link to="/privacy-policy" className={styles.footerLink}>Privacy</Link>.
        </div>
      </Form>

      <PrivacyPolicyModal open={privacyPolicyVisible} onCancel={() => setPrivacyPolicyVisible(false)} />
      <TermsOfServiceModal open={termsOfServiceVisible} onCancel={() => setTermsOfServiceVisible(false)} />
    </div>
  );
};

export default DealershipDetailsStep;
